import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { ExpandMore, ChevronRight } from '@material-ui/icons'
import { DocumentDownload, DrawerBodyWrapper, DrawerFooterWrapper, RadioButton, TOAST_TYPE, UploadMultipleFileButton } from '@base'
import { Button, Calendar, Input, Label } from '@baseV2'
import {
	BasicDetailsCard,
	CardHeader,
	CardTitle,
	CardTitleRow,
	StatusLabel,
	HeaderRight,
	ChevronBtn,
	SectionDivider,
	DocTilesRow,
	DocTile,
	Note,
	EmptyState,
} from '@pages/Admin/Orders/ManageOrder/EditInvoice/InvoiceDetails/Documents/style'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { updateOrganisationApi } from '@data/conn/put/organisation'
import { uploadInterestPaymentDocuments } from '@data'
import { strings } from '@constants'
import { Spacings, Text } from '@styles'

const FormWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};

	${Label} {
		text-transform: none;
	}
`

const RowWrapper = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	gap: ${Spacings.SPACING_3B};
	& > div {
		flex: 1;
	}
`

const InterestReceivedRow = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: ${Spacings.SPACING_4B};
`

const RadioGroupRow = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	column-gap: ${Spacings.SPACING_8B};
	min-height: ${Spacings.SPACING_10B};
`

const RadioOption = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	column-gap: ${Spacings.SPACING_2B};
`

const RadioOptionLabel = styled.span`
	font-size: ${Text.MEDIUM};
	color: ${({ theme }) => theme.palette.text.darkblack};
`

const FooterActions = styled.div`
	display: flex;
	width: 100%;
	gap: ${Spacings.SPACING_3B};
	justify-content: flex-end;
`

const deriveFormState = (orgDetails) => ({
	interest: {
		id: 'interest',
		label: 'Interest (%)',
		placeholder: strings('enter', 'interest'),
		value: orgDetails?.interest_payment?.interest ?? '',
		required: true,
	},
	interest_received: {
		value: Boolean(orgDetails?.interest_payment?.interest_received),
	},
	documents: {
		value: orgDetails?.interest_payment?.documents ?? [],
	},
	interest_payment_date: {
		id: 'interest-payment-date',
		label: 'Applicable From',
		placeholder: strings(['select', 'starting', 'date']),
		value: orgDetails?.interest_payment?.interest_payment_date ?? null,
		required: false,
	},
})

const EditInterestReceived = ({ orgDetails, update }) => {
	const dispatch = useDispatch()
	const [formState, setFormState] = useState(deriveFormState(orgDetails))
	const [isOpen, setIsOpen] = useState(Boolean(orgDetails?.interest_payment?.interest_payment_date))

	useEffect(() => {
		setFormState(deriveFormState(orgDetails))
		setIsOpen(Boolean(orgDetails?.interest_payment?.interest_payment_date))
	}, [orgDetails])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({ ..._s, [key]: { ..._s[key], value } }))
	}

	const interestReceivedNoHandler = () => {
		setFormState((_s) => ({
			..._s,
			interest_received: { ..._s.interest_received, value: false },
			interest: { ..._s.interest, value: '' },
			interest_payment_date: { ..._s.interest_payment_date, value: null },
			documents: { ..._s.documents, value: [] },
		}))
	}

	const uploadDocumentsHandler = (files) => {
		if (!files || files.length < 1) return
		dispatch(showLoader(true))
		Promise.all(Array.from(files).map((file) => uploadInterestPaymentDocuments(file, orgDetails?.organisation_id)))
			.then((responses) => {
				setFormState((_s) => ({
					..._s,
					documents: { ..._s.documents, value: [..._s.documents.value, ...responses.map((res) => res?.file_paths)]?.flat() },
				}))
				dispatch(showToast(true, strings('uploaded_the_file_successfully'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((err) => {
				console.log(err)
				dispatch(showToast(true, strings('document_upload_failed'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	const removeDocumentHandler = (index) => {
		setFormState((_s) => ({
			..._s,
			documents: { ..._s.documents, value: _s.documents.value.filter((_, _index) => _index !== index) },
		}))
	}
	const onInterestUpdate = () => {
		const { interest, interest_received, documents, interest_payment_date } = formState
		if (interest_received.value === true && !interest.value) return
		dispatch(showLoader(true))
		updateOrganisationApi(orgDetails.organisation_id, 'INTEREST_PAYMENT', null, {
			interest: Number(interest.value),
			interest_received: interest_received.value,
			documents: documents.value,
			interest_payment_date: interest_payment_date.value,
		})
			.then(() => {
				dispatch(showToast(true, strings(['successfully', 'updated', 'interest_payment']), { type: TOAST_TYPE.SUCCESS }))
				dispatch(toggleDrawer(false))
				update()
			})
			.catch((err) => {
				console.log(err)
				dispatch(showToast(true, 'Error while updating interest payment.', { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	const hasDocs = formState.documents.value.length > 0
	const statusLabel = hasDocs
		? `(${formState.documents.value.length}-${formState.documents.value.length > 1 ? strings('documents') : strings('document')})`
		: null

	return (
		<>
			<DrawerBodyWrapper>
				<InterestReceivedRow>
					<Label>Interest approval recd. from Client :</Label>
					<RadioGroupRow>
						<RadioOption>
							<RadioButton
								id='interest-received-yes'
								size='1.125'
								type='bool'
								checked={formState.interest_received.value === true}
								value={true}
								onChange={() => updateState('interest_received')(true)}
							/>
							<RadioOptionLabel>{strings('yes')}</RadioOptionLabel>
						</RadioOption>
						<RadioOption>
							<RadioButton
								id='interest-received-no'
								size='1.125'
								type='bool'
								checked={formState.interest_received.value === false}
								value={false}
								onChange={interestReceivedNoHandler}
							/>
							<RadioOptionLabel>{strings('no')}</RadioOptionLabel>
						</RadioOption>
					</RadioGroupRow>
				</InterestReceivedRow>
				<FormWrapper>
					<RowWrapper>
						<Input
							id={formState.interest.id}
							type='number'
							label={formState.interest.label}
							placeholder={formState.interest.placeholder}
							value={formState.interest.value}
							onChange={(e) => updateState('interest')(e.target.value)}
							required={formState.interest.required}
							disabled={formState.interest_received.value === false}
						/>
						<Calendar
							id={formState.interest_payment_date.id}
							label={formState.interest_payment_date.label}
							placeholder={formState.interest_payment_date.placeholder}
							value={formState.interest_payment_date.value}
							onChange={updateState('interest_payment_date')}
							maxDate={new Date()}
							required={formState.interest_payment_date.required}
							disabled={formState.interest_received.value === false}
						/>
					</RowWrapper>
					<BasicDetailsCard>
						<CardHeader $noBorder>
							<CardTitleRow>
								<CardTitle>{strings('documents')}</CardTitle>
								<StatusLabel $hasDocs={hasDocs}>{statusLabel}</StatusLabel>
							</CardTitleRow>
							<HeaderRight>
								<UploadMultipleFileButton
									xs
									type='secondary'
									label={strings('upload', 'documents')}
									onChange={uploadDocumentsHandler}
									disabled={formState.interest_received.value === false}
								/>
								<ChevronBtn onClick={() => setIsOpen((v) => !v)}>{isOpen ? <ExpandMore /> : <ChevronRight />}</ChevronBtn>
							</HeaderRight>
						</CardHeader>
						{isOpen &&
							formState.interest_received.value === true &&
							(hasDocs ? (
								<>
									<SectionDivider />
									<DocTilesRow>
										{formState.documents.value.map((item, index) => (
											<DocTile key={`interest-document-${index}`}>
												<DocumentDownload link={item} type='interest_payment' onDelete={() => removeDocumentHandler(index)} />
											</DocTile>
										))}
									</DocTilesRow>
									<Note>{`${strings('note')}: ${strings('click_icon_to_download')}`}</Note>
								</>
							) : (
								<EmptyState>
									<span>{strings('no_document_has_been_uploaded')}</span>
									<span>{strings('click_upload_to_add_document')}</span>
								</EmptyState>
							))}
					</BasicDetailsCard>
				</FormWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<FooterActions>
					<Button variant='outline' color='danger' onClick={() => dispatch(toggleDrawer(false))}>
						{strings('cancel')}
					</Button>
					<Button disabled={formState.interest_received.value === true && !formState.interest.value} onClick={onInterestUpdate}>
						{strings('update')}
					</Button>
				</FooterActions>
			</DrawerFooterWrapper>
		</>
	)
}

export default EditInterestReceived
